import './associate-device-form.scss';
import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useHistory} from 'react-router-dom';
import {Device} from '../../backend/interfaces/device';
import {PutDevice} from '../../backend/requests/put-device';
import {useSnackBar} from '../../contexts/snack-bar/context';
import {translations} from '../../i18n/translations';
import {DevicesRepository} from '../../repositories/devices';
import {ContentPage} from '../content-page/content-page';
import {PageHeader} from '../page-header/page-header';
import {RouterPrompt} from '../router-prompt/router-prompt';
import {CaregiverMode} from '../../enums/caregiver-mode';
import {AffiliationInfo} from '../../backend/interfaces/affiliation-info';
import {UsersRepository} from '../../repositories/users';
import {Alert, Box, Stack, Typography} from '@mui/material';
import {LoadingContent} from '../loading-content/loading-content';
import {getBrandName} from '../../utils/functions/get-product-name';
import {useAppContext} from '../../contexts/app/context';
import {useFetchAllItems} from '../../hooks/fetch-all-items';
import {FlatSelectDropdown} from '../flat-select-dropdown/flat-select-dropdown';
import FlatInput from '../flat-input/flat-input';
import {Button} from '../button/button';
import {Modal} from '../modal/modal';
import {DeviceWorkgroup} from '../../backend/enums/device-workgroup';

// Hold variables
const BATCH_SIZE: number = 10;

/**
 * Form to associate a device to a user.
 *
 * It uses the office context to handle the form state.
 *
 * Permissions are given by props.
 *
 * This form was created taking into account that it will exist under the senior tabs.
 * Later on, if it's necessary, we can extend this component to be applied to more contexts.
 */
export const AssociateDeviceForm = (props: {
  caregiverMode: CaregiverMode.Professional | CaregiverMode.SystemAdministrator;
  userId: string;
  userName: string;
  redirectUrl: string;
  entityId?: string;
}) => {
  const snackBarContext = useSnackBar();
  const appContext = useAppContext();

  // Hold state
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState<boolean>(true);
  const [submitDialogOpened, setSubmitDialogOpened] = useState<boolean>(false);
  const [dataEntered, setDataEntered] = useState<boolean>(false);
  const [device, setDevice] = useState<Device | undefined>();
  const [userAffiliations, setUserAffiliations] = useState<
    | {
        affiliations: Array<AffiliationInfo>;
        totalAffiliations: number;
        selectedAffiliation?: AffiliationInfo;
      }
    | undefined
  >();
  const [affiliationError, setAffiliationError] = useState<string | undefined>();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const {t} = useTranslation();
  let history = useHistory();
  const {items: devices, isLoading} = useFetchAllItems({
    fetchFunction: async ({limit, skip}) => {
      if (!userAffiliations?.selectedAffiliation?.entity.id) {
        return Promise.resolve({data: [], totalCount: 0});
      }
      const response = await DevicesRepository.instance.getInEntityAsAffiliatedForEntity(
        userAffiliations?.selectedAffiliation?.entity.id,
        limit,
        skip,
        true,
        undefined,
        DeviceWorkgroup.DigitalInclusion
      );

      return {
        data: response.data.map((obj) => {
          return {id: obj.id || '', name: obj.serialNumber || obj.macAddress || '', item: obj};
        }),
        totalCount: response.totalCount,
      };
    },
    deps: [userAffiliations?.selectedAffiliation],
  });

  // Get the first batch of user affiliations
  useEffect(() => {
    const getUserAffiliations = async () => {
      try {
        // Get first batch of affiliations
        const result = await UsersRepository.instance.getUserAffiliations(
          props.userId,
          0,
          BATCH_SIZE
        );

        // Set affiliations
        if (result) {
          setUserAffiliations({
            affiliations: result.data,
            selectedAffiliation: result.data[0],
            totalAffiliations: result.totalCount,
          });
        } else {
          // Error
          throw new Error('Empty user affiliations.');
        }
      } catch (error) {
        // Log
        console.debug('🔴 Could not get the user affiliations.', error);

        // Set user affiliations
        setUserAffiliations({
          affiliations: [],
          totalAffiliations: 0,
        });
      }
    };

    if (props.caregiverMode === CaregiverMode.SystemAdministrator) {
      getUserAffiliations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.caregiverMode, props.userId]);

  // Validation
  useEffect(() => {
    // Holds validation
    let deviceValid: boolean = false;
    let affiliationValid: boolean = false;

    // Device validation
    if (device) {
      // Valid
      deviceValid = true;
    } else {
      // Invalid
      deviceValid = false;
    }

    // Affiliation validation
    if (
      userAffiliations?.selectedAffiliation ||
      props.caregiverMode === CaregiverMode.Professional
    ) {
      // No errors
      if (affiliationError !== undefined) {
        setAffiliationError(undefined);
      }

      // Valid
      affiliationValid = true;
    } else {
      // Errors
      setAffiliationError(
        t(translations.associate_device_form.errors.form_validation_invalid_field)
      );

      // Invalid
      affiliationValid = false;
    }

    // Data was entered
    if (device) {
      setDataEntered(true);
    } else {
      setDataEntered(false);
    }

    // Submit button validation
    if (deviceValid && affiliationValid) {
      // Enable submit button
      setSubmitButtonDisabled(false);
    } else {
      // Disable submit button
      setSubmitButtonDisabled(true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device, userAffiliations, affiliationError, props.caregiverMode]);

  // Submit
  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      // Verify but it's not expected
      if (!device || !device.id) {
        // Throw error
        throw new Error('Empty device.');
      }

      // Request body
      const requestBody: PutDevice = {
        isActive: device.isActive,
        product: device.product,
        type: device.type,
        userId: props.userId,
      };

      try {
        // Associate device
        await DevicesRepository.instance.updateById(device.id, requestBody);

        setSubmitDialogOpened(false);
        setDataEntered(false);
        setIsSubmitting(false);
        snackBarContext.setOfficeToastNotification({
          severity: 'success',
          message: t(translations.associate_device_form.success.success_message, {
            name: props.userName,
          }),
        });
        // Wait some time just to make sure the button state was updated
        // so the router prompt won't appear when the history is updated.
        setTimeout(() => {
          // Redirect
          history.push(props.redirectUrl);
        }, 500);
      } catch (e) {
        console.error('Error associating device', e);
        setSubmitDialogOpened(false);
        setIsSubmitting(false);
      }
    } catch (error) {
      // Log
      console.debug('🔴 Could not associate the device.', error);
    }
  };

  return (
    <ContentPage>
      {props.caregiverMode === CaregiverMode.SystemAdministrator && !userAffiliations && (
        <LoadingContent marginTop="32px" />
      )}
      {((props.caregiverMode === CaregiverMode.Professional && !props.entityId) ||
        (props.caregiverMode === CaregiverMode.SystemAdministrator &&
          userAffiliations &&
          userAffiliations.affiliations.length === 0)) && (
        <Alert severity="info" className="associate-device-form-info-message">
          {t(translations.associate_device_form.no_affiliations_info_message)}
        </Alert>
      )}
      {((props.caregiverMode === CaregiverMode.Professional && props.entityId) ||
        (props.caregiverMode === CaregiverMode.SystemAdministrator &&
          userAffiliations &&
          userAffiliations.affiliations.length > 0)) &&
        userAffiliations?.selectedAffiliation && (
          <>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                setSubmitDialogOpened(true);
              }}
            >
              <PageHeader title={t(translations.associate_device_form.title)} isSmallTitle={true} />
              {props.caregiverMode === CaregiverMode.SystemAdministrator && userAffiliations && (
                <FlatInput
                  type="text"
                  value={userAffiliations.selectedAffiliation?.entity.name}
                  disabled
                  errors={affiliationError}
                />
              )}
              <Box className="associate-device-form-field">
                <FlatSelectDropdown
                  label={t(translations.associate_device_form.identifier_input)}
                  loading={isLoading}
                  required
                  value={
                    device
                      ? {
                          key: device.id || '',
                          label: device.serialNumber || device.macAddress || '',
                        }
                      : undefined
                  }
                  options={
                    !devices ? [] : devices.map((device) => ({key: device.id, label: device.name}))
                  }
                  onChange={(value) => {
                    if (!value.key) {
                      setDevice(undefined);
                    } else {
                      const find = devices?.find((device) => device.id === value.key);
                      if (find) {
                        setDevice({
                          id: find.id,
                          serialNumber: find.item.serialNumber,
                          macAddress: find.item.macAddress,
                          isActive: find.item.isActive,
                          product: find.item.product,
                          type: find.item.type,
                        });
                      } else {
                        setDevice(undefined);
                      }
                    }
                  }}
                />
              </Box>
              <Stack
                className="associate-device-form-footer"
                flexDirection="row"
                gap="10px"
                justifyContent="space-between"
              >
                <Button
                  variant="text"
                  color="error"
                  sx={{
                    width: '100%',
                    height: '40px',
                    padding: '8px 16px',
                    borderRadius: '5px',
                    maxWidth: '252px',
                  }}
                  onClick={() => {
                    history.push(props.redirectUrl);
                  }}
                  disabled={isSubmitting}
                >
                  {t(translations.user_form.cancel_button)}
                </Button>
                <Button
                  sx={{
                    width: '100%',
                    maxWidth: '252px',
                  }}
                  variant="contained"
                  disabled={submitButtonDisabled || isSubmitting}
                  type="submit"
                >
                  {t(translations.device_form.save_button)}
                </Button>
              </Stack>
            </form>
            <Modal
              open={submitDialogOpened}
              maxWidth="sm"
              fullWidth
              className="associate-device-form-modal"
              preventCloseClick
              onClose={() => setSubmitDialogOpened(false)}
              actions={[
                {
                  label: t(translations.modal.cancel),
                  type: 'destructive',
                  onClick: () => setSubmitDialogOpened(false),
                  disabled: isSubmitting,
                },
                {
                  label: t(translations.device_form.save_button),
                  type: 'nondestructive',
                  onClick: handleSubmit,
                  loading: isSubmitting,
                  disabled: isSubmitting,
                },
              ]}
            >
              <Typography className="title">
                {t(translations.associate_device_form.save_dialog_title, {
                  name: props.userName,
                  device: device?.serialNumber || device?.macAddress || '',
                })}
              </Typography>
              <Typography className="subtitle">
                {t(translations.associate_device_form.save_dialog_message, {
                  name: props.userName,
                  brand: getBrandName(appContext.state.configuration.product),
                })}
              </Typography>
            </Modal>
            <RouterPrompt
              when={dataEntered && !isSubmitting}
              subtitle={t(translations.associate_device_form.router_prompt_title)}
              message={t(translations.associate_device_form.router_prompt_message)}
              onOK={() => true}
              onCancel={() => false}
              toastNotification={{
                severity: 'info',
                message: t(translations.associate_device_form.cancel_toast),
              }}
            />
          </>
        )}
    </ContentPage>
  );
};
